import React, {useRef, useState, useEffect, useMemo} from 'react';
import ProductCard from './ProductCard';
import SectionContainer from '../common/SectionContainer';
import DashboardButton from '../common/DashboardButton';
import myLabsRx from '../../assets/dashboard/my_labs_rx.png';
import {Recommendation} from '../../types/api';

// Static My Labs Rx card - always shown first
const myLabsRxCard = {
    name: 'My Labs Rx',
    category: 'You Qualify',
    description: 'Unlock in-depth lab features and a quarterly lab kit curated for your unique health needs and goals.',
    price: '$99.00/mo',
    image: myLabsRx,
};

interface RecommendationsProps {
    recommendations?: Recommendation[];
    loading?: boolean;
    isSubscribed?: boolean;
}

const Recommendations: React.FC<RecommendationsProps> = ({
    recommendations = [],
    loading = false,
    isSubscribed = false,
}) => {
    const scrollContainerRef = useRef<HTMLDivElement>(null);
    const [showScrollButtons, setShowScrollButtons] = useState(false);

    // Helper function to strip HTML tags and get plain text
    const stripHtml = (html: string): string => {
        const div = document.createElement('div');
        div.innerHTML = html;
        return div.textContent || div.innerText || '';
    };

    // Convert API recommendations to product card format and combine with My Labs Rx
    const allProducts = useMemo(() => {
        const apiProducts = recommendations.map((rec) => ({
            name: rec.name,
            category: rec.sup_type || 'Supplement',
            description: rec.content ? stripHtml(rec.content) : 'Based on your lab results',
            price: rec.price ? `Starts at $${rec.price.toFixed(2)}` : 'Contact for pricing',
            discountPrice: rec.members_price ? rec.members_price.toFixed(2) : undefined,
            image: rec.image || '',
            url: rec.url,
        }));

        // Combine My Labs Rx (first) with API recommendations - exclude My Labs Rx if subscribed
        return isSubscribed ? apiProducts : [myLabsRxCard, ...apiProducts];
    }, [recommendations, isSubscribed]);

    // Check if scrolling is needed
    useEffect(() => {
        const checkScrollNeeded = () => {
            if (scrollContainerRef.current) {
                const {scrollWidth, clientWidth} = scrollContainerRef.current;
                setShowScrollButtons(scrollWidth > clientWidth);
            }
        };

        checkScrollNeeded();
        window.addEventListener('resize', checkScrollNeeded);
        return () => window.removeEventListener('resize', checkScrollNeeded);
    }, [allProducts]);

    const scrollLeft = () => {
        if (scrollContainerRef.current) {
            scrollContainerRef.current.scrollBy({
                left: -283, // Scroll by width of one card plus gap (263px + 20px gap)
                behavior: 'smooth',
            });
        }
    };

    const scrollRight = () => {
        if (scrollContainerRef.current) {
            scrollContainerRef.current.scrollBy({
                left: 283, // Scroll by width of one card plus gap (263px + 20px gap)
                behavior: 'smooth',
            });
        }
    };

    return (
        <SectionContainer title="🧬 BioMatch™: Personalized Recommendations, Powered by Your Blood">
            {loading ? (
                <div className="text-center py-8 text-mld-card-text">Loading recommendations...</div>
            ) : (
                <>
                    <div className="relative">
                        {/* Navigation arrows */}
                        {showScrollButtons && (
                            <>
                                <button
                                    onClick={scrollLeft}
                                    className="absolute left-[-18px] top-1/2 -translate-y-1/2 z-10 w-5 h-5 bg-mld-panel-bg rounded-full flex items-center justify-center text-mld-card-text hover:text-mld-primary transition-colors border border-mld-panel-border">
                                    <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                            strokeWidth={2}
                                            d="M15 19l-7-7 7-7"
                                        />
                                    </svg>
                                </button>

                                <button
                                    onClick={scrollRight}
                                    className="absolute right-[-18px] top-1/2 -translate-y-1/2 z-10 w-5 h-5 bg-mld-panel-bg rounded-full flex items-center justify-center text-mld-card-text hover:text-mld-primary transition-colors border border-mld-panel-border">
                                    <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                            strokeWidth={2}
                                            d="M9 5l7 7-7 7"
                                        />
                                    </svg>
                                </button>
                            </>
                        )}

                        {/* Product cards */}
                        <div
                            ref={scrollContainerRef}
                            className="flex gap-4 overflow-x-auto pb-2 pl-6 pr-10 scrollbar-hide">
                            {allProducts.map((product, index) => (
                                <ProductCard key={index} product={product} />
                            ))}
                        </div>
                    </div>

                    {/* See More button */}
                    <div className="flex justify-end mt-4">
                        <DashboardButton text="See More" />
                    </div>
                </>
            )}
        </SectionContainer>
    );
};

export default Recommendations;
